import dayjs from "dayjs";
import * as XLSX from "xlsx";

import type { IncomeStatementGroupedData } from "~/types/contabilidad";

interface IncomeStatementExportData {
  data: IncomeStatementGroupedData;
  year: number;
  month: number;
  companyName: string;
  templateName: string;
}

const MONTH_NAMES = [
  "Enero",
  "Febrero",
  "Marzo",
  "Abril",
  "Mayo",
  "Junio",
  "Julio",
  "Agosto",
  "Septiembre",
  "Octubre",
  "Noviembre",
  "Diciembre",
];

function formatNumber(value: number): number {
  return Math.round(value);
}

/**
 * Exports the Income Statement (Estado de Resultados) to Excel with 3-column format
 */
export const exportIncomeStatementToExcel = ({
  data,
  year,
  month,
  companyName,
  templateName,
}: IncomeStatementExportData): void => {
  const workbook = XLSX.utils.book_new();

  // Header section data
  const headerData: (string | number)[][] = [
    ["ESTADO DE RESULTADOS"],
    [],
    ["Empresa:", companyName],
    ["Año:", year],
    ["Mes:", MONTH_NAMES[month - 1]],
    ["Plantilla:", templateName],
    ["Fecha de generación:", dayjs().format("DD/MM/YYYY HH:mm")],
    [],
    ["Descripción", "Acumulado Mes Anterior", "Mes Actual", "Acumulado"],
  ];

  // Build hierarchical data with 3 columns
  const sheetRows: (string | number)[][] = [];

  for (const clase of data.clases) {
    // CLASE header row (Level 1)
    sheetRows.push([clase.clase.toUpperCase(), "", "", ""]);

    for (const subClase of clase.subClases) {
      // SUB_CLASE header row (Level 2)
      sheetRows.push(["  " + subClase.sub_clase, "", "", ""]);

      for (const subClase2 of subClase.subClases2) {
        // SUB_CLASE_2 header row (Level 3)
        sheetRows.push(["    " + subClase2.sub_clase_2, "", "", ""]);

        for (const grupo of subClase2.grupos) {
          // GRUPO header row (Level 4) - only if named
          if (grupo.grupo_cuenta_name) {
            const grupoLabel = grupo.grupo_cuenta_code
              ? `${grupo.grupo_cuenta_code} - ${grupo.grupo_cuenta_name}`
              : grupo.grupo_cuenta_name;
            sheetRows.push(["      " + grupoLabel, "", "", ""]);
          }

          // Individual accounts (Level 5)
          for (const account of grupo.accounts) {
            const indent = grupo.grupo_cuenta_name ? "        " : "      ";
            sheetRows.push([
              `${indent}${account.account_code} - ${account.account_name}`,
              formatNumber(account.anterior),
              formatNumber(account.actual),
              formatNumber(account.total),
            ]);
          }

          // GRUPO total row (only if named)
          if (grupo.grupo_cuenta_name) {
            sheetRows.push([
              "      Total " + grupo.grupo_cuenta_name,
              formatNumber(grupo.anterior),
              formatNumber(grupo.actual),
              formatNumber(grupo.total),
            ]);
          }
        }

        // SUB_CLASE_2 total row
        sheetRows.push([
          "    Total " + subClase2.sub_clase_2,
          formatNumber(subClase2.anterior),
          formatNumber(subClase2.actual),
          formatNumber(subClase2.total),
        ]);
      }

      // SUB_CLASE total row
      sheetRows.push([
        "  Total " + subClase.sub_clase,
        formatNumber(subClase.anterior),
        formatNumber(subClase.actual),
        formatNumber(subClase.total),
      ]);
    }

    // CLASE total row
    sheetRows.push([
      "TOTAL " + clase.clase.toUpperCase(),
      formatNumber(clase.anterior),
      formatNumber(clase.actual),
      formatNumber(clase.total),
    ]);

    // Empty row between classes
    sheetRows.push(["", "", "", ""]);
  }

  // Summary section
  sheetRows.push(["", "", "", ""]);
  sheetRows.push(["RESUMEN", "", "", ""]);
  sheetRows.push([
    "Total Ingresos",
    formatNumber(data.totals.ingresos.anterior),
    formatNumber(data.totals.ingresos.actual),
    formatNumber(data.totals.ingresos.total),
  ]);
  sheetRows.push([
    "Total Costos",
    formatNumber(data.totals.costos.anterior),
    formatNumber(data.totals.costos.actual),
    formatNumber(data.totals.costos.total),
  ]);
  sheetRows.push([
    "Utilidad Bruta",
    formatNumber(data.totals.utilidadBruta.anterior),
    formatNumber(data.totals.utilidadBruta.actual),
    formatNumber(data.totals.utilidadBruta.total),
  ]);
  sheetRows.push([
    "Total Gastos",
    formatNumber(data.totals.gastos.anterior),
    formatNumber(data.totals.gastos.actual),
    formatNumber(data.totals.gastos.total),
  ]);
  sheetRows.push([
    "Utilidad Operacional",
    formatNumber(data.totals.utilidadOperacional.anterior),
    formatNumber(data.totals.utilidadOperacional.actual),
    formatNumber(data.totals.utilidadOperacional.total),
  ]);
  sheetRows.push(["", "", "", ""]);
  sheetRows.push([
    "Margen Operacional %",
    "",
    "",
    data.totals.margenOperacional.toFixed(2) + "%",
  ]);

  // Combine all data
  const sheetData = [...headerData, ...sheetRows];

  // Create worksheet
  const worksheet = XLSX.utils.aoa_to_sheet(sheetData);

  // Set column widths
  worksheet["!cols"] = [
    { wch: 60 }, // Descripción
    { wch: 25 }, // Acumulado Mes Anterior
    { wch: 20 }, // Mes Actual
    { wch: 20 }, // Acumulado
  ];

  // Add worksheet to workbook
  XLSX.utils.book_append_sheet(workbook, worksheet, "Estado de Resultados");

  // Generate filename
  const monthName = MONTH_NAMES[month - 1].toLowerCase();
  const filename = `estado_resultados_${year}_${monthName}_${dayjs().format("YYYY-MM-DD")}.xlsx`;

  // Download file
  XLSX.writeFile(workbook, filename);
};
